import React, { useState, useEffect } from 'react';
import { Table, Card, message, Empty, Select, Input, Form, Col, Tag } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import FilterPanel from '../../components/common/FilterPanel';
import { itemLocationRelationshipService } from '../../services/itemLocationRelationshipService';
import { itemService } from '../../services/itemService';
import { warehouseService } from '../../services/warehouseService';
import dayjs from 'dayjs';
import {
  ItemLocationRelationship,
  ItemLocationRelationshipFilters,
  ItemLocationRelationshipPaginationParams,
  ItemLocationRelationshipSortField,
  SortOrder,
  Warehouse
} from '../../types/item-location-relationship';
import { Item } from '../../types/item';
import '../../styles/itemLocationRelationship.css';

const { Option } = Select;

const ItemLocationRelationshipList: React.FC = () => {
  const { t } = useTranslation();
  
  // State management
  const [loading, setLoading] = useState<boolean>(false);
  const [warehouseOptionsLoading, setWarehouseOptionsLoading] = useState<boolean>(false);
  const [itemOptionsLoading, setItemOptionsLoading] = useState<boolean>(false);
  const [itemLocationRelationships, setItemLocationRelationships] = useState<ItemLocationRelationship[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [current, setCurrent] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [filters, setFilters] = useState<ItemLocationRelationshipFilters>({});
  const [sortField, setSortField] = useState<ItemLocationRelationshipSortField>();
  const [sortOrder, setSortOrder] = useState<'ascend' | 'descend'>('descend');
  
  // Options for dropdowns
  const [warehouseOptions, setWarehouseOptions] = useState<Warehouse[]>([]);
  const [itemOptions, setItemOptions] = useState<Item[]>([]);

  // Fetch item location relationships data
  const fetchItemLocationRelationships = async () => {
    setLoading(true);
    try {
      const response = await itemLocationRelationshipService.getItemLocationRelationships(
        filters,
        {
          currentPage: current,
          pageSize,
          sortBy: sortField,
          sortOrder: sortOrder === 'ascend' ? 'asc' : 'desc'
        }
      );

      if (response.success && response.data) {
        setItemLocationRelationships(response.data.tableData);
        setTotal(response.data.paging.totalCount);
      } else {
        console.error(t('itemLocationRelationship.list.messages.dataFormatError'), response);
        setItemLocationRelationships([]);
        setTotal(0);
        if (response.errorMessage) {
          message.error(response.errorMessage);
        }
      }
    } catch (error) {
      message.error(t('itemLocationRelationship.list.messages.fetchFailed'));
      console.error(t('itemLocationRelationship.list.messages.fetchFailed'), error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch warehouse options
  const fetchWarehouseOptions = async (searchValue: string = '') => {
    setWarehouseOptionsLoading(true);
    try {
      const response = await warehouseService.listWarehouse({searchStr: searchValue});
      if (response.success && response.data) {
        // 转换数据格式：将warehouseService返回的数据转换为Warehouse类型
        const warehouses: Warehouse[] = response.data.map((item: any) => ({
          id: item.id,
          code: item.code,
          name: item.name,
          address: item.address,
          status: item.status
        }));
        setWarehouseOptions(warehouses);
      }
    } catch (error) {
      console.error(t('itemLocationRelationship.list.messages.fetchWarehouseOptionsFailed'), error);
    } finally {
      setWarehouseOptionsLoading(false);
    }
  };

  // Fetch item options
  const fetchItemOptions = async (searchValue: string = '') => {
    setItemOptionsLoading(true);
    try {
      let req = {
        searchStr: searchValue,
        pageSize: 100,
        currentPage: 1
      };
      const response = await itemService.listItems(req);
      if (response.success && response.data) {
        setItemOptions(response.data);
      }
    } catch (error) {
      console.error(t('itemLocationRelationship.list.messages.fetchItemOptionsFailed'), error);
    } finally {
      setItemOptionsLoading(false);
    }
  };

  useEffect(() => {
    fetchItemLocationRelationships();
  }, [current, pageSize, filters, sortField, sortOrder]);

  useEffect(() => {
    fetchWarehouseOptions();
    fetchItemOptions();
  }, []);

  const handleTableChange = (
    pagination: any,
    _filters: any,
    sorter: any
  ) => {
    setCurrent(pagination.current);
    setPageSize(pagination.pageSize);

    if (sorter.field && sorter.order) {
      setSortField(sorter.field);
      setSortOrder(sorter.order);
    }
  };

  const handleSearch = (values: any) => {
    setFilters({ ...filters, ...values });
    setCurrent(1); // 重置到第一页
  };

  const handleReset = () => {
    setFilters({});
    setCurrent(1);
  };

  // Format datetime display
  const formatDateTime = (timestamp: number): string => {
    return dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss');
  };

  const columns: ColumnsType<ItemLocationRelationship> = [
    {
      title: t('itemLocationRelationship.list.columns.warehouseCode'),
      dataIndex: 'warehouseCode',
      key: 'warehouseCode',
      width: 120,
    },
    {
      title: t('itemLocationRelationship.list.columns.ownerCode'),
      dataIndex: 'ownerCode',
      key: 'ownerCode',
      width: 120,
    },
    {
      title: t('itemLocationRelationship.list.columns.itemCode'),
      dataIndex: 'itemCode',
      key: 'itemCode',
      width: 120,
    },
    {
      title: t('itemLocationRelationship.list.columns.itemName'),
      dataIndex: ['itemDTO', 'name'],
      key: 'itemName',
      render: (_, record) => record.itemDTO?.name || '-',
      width: 150,
    },
      {
      title: t('itemLocationRelationship.list.columns.locationCode'),
      dataIndex: 'locationCode',
      key: 'locationCode',
      width: 150,
    },
    {
      title: t('itemLocationRelationship.list.columns.quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
      width: 150,
    },
     {
      title: t('itemLocationRelationship.list.columns.preQuantity'),
      dataIndex: 'preQuantity',
      key: 'preQuantity',
      width: 150,
    },
    {
      title: t('itemLocationRelationship.list.columns.shelfCode'),
      dataIndex: 'shelfCode',
      key: 'shelfCode',
      width: 120,
    },
    {
      title: t('itemLocationRelationship.list.columns.layerNumber'),
      dataIndex: 'layerNumber',
      key: 'layerNumber',
      width: 100,
      align: 'center',
    },
    {
      title: t('itemLocationRelationship.list.columns.seqOnShelfLayer'),
      dataIndex: 'seqOnShelfLayer',
      key: 'seqOnShelfLayer',
      width: 120,
      align: 'center',
    },
    {
      title: t('itemLocationRelationship.list.columns.preLocationCodes'),
      dataIndex: 'preLocationCodes',
      key: 'preLocationCodes',
      width: 120,
      align: 'center',
    },
    // {
    //   title: t('itemLocationRelationship.list.columns.changed'),
    //   dataIndex: 'changed',
    //   key: 'changed',
    //   width: 120,
    //   align: 'center',
    //   render: (value: true | false) => (
    //     <Tag color={value === true ? 'green' : 'default'}>
    //       {value === true ? t('common.yes') : t('common.no')}
    //     </Tag>
    //   ),
    // },
    {
      title: t('itemLocationRelationship.list.columns.planogramCode'),
      dataIndex: 'planogramCode',
      key: 'planogramCode',
      width: 120,
      render: (text) => text || '-',
    },
    {
      title: t('itemLocationRelationship.list.columns.storeGroupCode'),
      dataIndex: 'storeGroupCode',
      key: 'storeGroupCode',
      width: 120,
      render: (text) => text || '-',
    },
    {
      title: t('itemLocationRelationship.list.columns.displayGroupCode'),
      dataIndex: 'displayGroupCode',
      key: 'displayGroupCode',
      width: 120,
      render: (text) => text || '-',
    },
    {
      title: t('itemLocationRelationship.list.columns.createTime'),
      dataIndex: 'gmtCreate',
      key: 'gmtCreate',
      render: (timestamp: number) => timestamp ? formatDateTime(timestamp) : '-',
      width: 180,
    },
    {
      title: t('itemLocationRelationship.list.columns.modifyTime'),
      dataIndex: 'gmtModified',
      key: 'gmtModified',
      render: (timestamp: number) => timestamp ? formatDateTime(timestamp) : '-',
      width: 180,
    },
  ];

  // Filter panel fields
  const renderFilterPanel = () => (
    <FilterPanel
      onFilter={handleSearch}
      onReset={handleReset}
      title={t('menu.itemLocationRelationship')}
      className="filter-card"
      loading={loading}
    >
      <Col span={6}>
        <Form.Item 
          name="warehouseCode" 
          label={t('itemLocationRelationship.list.filter.warehouse')}
        >
          <Select
            placeholder={t('itemLocationRelationship.list.filter.warehousePlaceholder')}
            allowClear
            showSearch
            loading={warehouseOptionsLoading}
            onSearch={fetchWarehouseOptions}
            filterOption={false}
            style={{ width: '100%' }}
          >
            {warehouseOptions.map(warehouse => (
              <Option key={warehouse.code} value={warehouse.code}>
                {warehouse.name} ({warehouse.code})
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item 
          name="itemCode" 
          label={t('itemLocationRelationship.list.filter.item')}
        >
          <Select
            placeholder={t('itemLocationRelationship.list.filter.itemPlaceholder')}
            allowClear
            showSearch
            loading={itemOptionsLoading}
            onSearch={fetchItemOptions}
            filterOption={false}
            style={{ width: '100%' }}
          >
            {itemOptions.map(item => (
              <Option key={item.code} value={item.code}>
                {item.name} ({item.code})
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item 
          name="shelfCode" 
          label={t('itemLocationRelationship.list.filter.shelf')}
        >
          <Input
            placeholder={t('itemLocationRelationship.list.filter.shelfPlaceholder')}
            allowClear
          />
        </Form.Item>
      </Col>
      {/* <Col span={6}>
        <Form.Item 
          name="changed" 
          label={t('itemLocationRelationship.list.columns.changed')}
        >
         <Select
            options={[
              { label: t('common.yes'), value: true },
              { label: t('common.no'), value: false },
            ]}
            allowClear
          />
        </Form.Item>
      </Col> */}
    </FilterPanel>
  );

  return (
    <div className="item-location-relationship-list">
      {renderFilterPanel()}
      <Card>
        <Table
          columns={columns}
          dataSource={itemLocationRelationships}
          loading={loading}
          rowKey="id"
          pagination={{
            current,
            pageSize,
            total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              t('itemLocationRelationship.list.pagination.displayRange', { start: range[0], end: range[1], total }),
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          onChange={handleTableChange}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t('common.noData')}
              />
            ),
          }}
          scroll={{ x: 1400, y: 500 }}
        />
      </Card>
    </div>
  );
};

export default ItemLocationRelationshipList;